import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { BookOpen, CheckCircle, CheckSquare, ChevronLeft, ChevronRight, ClipboardList, Download, FileImage, FileText, Loader2, RefreshCw, Search } from 'lucide-react'
import { usePermissions } from '../../hooks/usePermissions'
import {
  finishLearningQuestionnaire,
  getLearningUpdates,
  getKnowledgeArticle,
  getLearningQuestion,
  getLearningQuestionnaire,
  getLearningQuestionnaires,
  startLearningQuestionnaire,
  submitLearningAnswer,
  type KnowledgeArticle,
  type LearningQuestion,
  type LearningQuestionnaire,
  type LearningQuestionnaireResult,
} from '../../services/eagleVision'

type LearningTab = 'articles' | 'questionnaires'
type JsonRecord = Record<string, unknown>
const LEARNING_PAGE_SIZE = 10

function record(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : {}
}

function responseData<T>(value: unknown): T {
  const outer = record(value)
  return (outer.data !== undefined ? outer.data : value) as T
}

function itemId(value: unknown): number | null {
  const obj = record(value)
  const id = obj.id ?? obj.article_id ?? obj.questionnaire_id ?? obj.question_id ?? obj.option_id ?? obj.question_option_id ?? obj.answer_id
  const parsed = Number(id)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function htmlText(value: string): string {
  if (!value.includes('<')) return value
  const parsed = new DOMParser().parseFromString(value, 'text/html')
  const blockTags = new Set(['ADDRESS', 'ARTICLE', 'BLOCKQUOTE', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'OL', 'P', 'PRE', 'SECTION', 'UL'])

  const textFromNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || ''
    if (node.nodeName === 'BR') return '\n'

    const text = Array.from(node.childNodes).map(textFromNode).join('')
    return blockTags.has(node.nodeName) ? `${text}\n` : text
  }

  return textFromNode(parsed.body)
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function textField(obj: JsonRecord, ...keys: string[]): string {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'string' && value.trim()) return value
  }
  return ''
}

function normalizeQuestion(value: unknown, fallbackId?: number): LearningQuestion | null {
  const outer = record(value)
  const nested = record(outer.question)
  const question = Object.keys(nested).length ? nested : outer
  const id = itemId(question) ?? fallbackId ?? itemId(outer)
  const questionText = textField(question, 'question', 'text', 'title', 'description') ||
    (typeof outer.question === 'string' ? outer.question : '')
  const rawOptions = question.options ?? question.question_options ?? question.answers ?? outer.options
  if (!id || !questionText || !Array.isArray(rawOptions)) return null

  const options = rawOptions.map((raw: unknown) => {
    const option = record(raw)
    return {
      ...option,
      id: itemId(option) ?? 0,
      option: typeof raw === 'string' ? raw : textField(option, 'option', 'option_text', 'name', 'title', 'label', 'value', 'answer'),
    }
  })

  return {
    id,
    question: questionText,
    note: textField(question, 'note', 'description') || null,
    options,
  }
}

function getQuestionIds(value: unknown): number[] {
  const data = record(responseData<unknown>(value))
  const rawQuestions = data.questions ?? data.questionnaire_questions ?? data.questionnaire_question ?? data.question_ids ?? data.question_list
  if (!Array.isArray(rawQuestions)) return []
  return rawQuestions.map((question: unknown) => {
    if (typeof question === 'number' || typeof question === 'string') {
      const id = Number(question)
      return Number.isInteger(id) && id > 0 ? id : null
    }
    return itemId(question)
  }).filter((id: number | null): id is number => id !== null)
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response
    if (response?.data?.message) return response.data.message
  }
  return error instanceof Error && error.message ? error.message : fallback
}

export default function LearningPage() {
  const { can } = usePermissions()
  const location = useLocation()
  const navigate = useNavigate()
  const canViewArticles = can('pwa_learning', 'view')
  const canViewQuestionnaires = can('pwa_learning', 'view')
  const canAnswerQuestionnaires = can('pwa_learning', 'answer')
  const questionnaireTargetParam = new URLSearchParams(location.search).get('questionnaire_id')
  const requestedQuestionnaireId = Number(questionnaireTargetParam)
  const hasQuestionnaireTarget = !!questionnaireTargetParam && Number.isInteger(requestedQuestionnaireId) && requestedQuestionnaireId > 0
  const [tab, setTab] = useState<LearningTab>(() => {
    const requestedTab = new URLSearchParams(window.location.search).get('tab')
    return requestedTab === 'questionnaires' || !canViewArticles ? 'questionnaires' : 'articles'
  })
  const [articles, setArticles] = useState<KnowledgeArticle[]>([])
  const [questionnaires, setQuestionnaires] = useState<LearningQuestionnaire[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null)
  const [activeQuestionnaire, setActiveQuestionnaire] = useState<LearningQuestionnaire | null>(null)
  const [questions, setQuestions] = useState<LearningQuestion[]>([])
  const [questionIndex, setQuestionIndex] = useState(0)
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [complete, setComplete] = useState(false)
  const [questionnaireResult, setQuestionnaireResult] = useState<LearningQuestionnaireResult | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [articlePage, setArticlePage] = useState(1)
  const [questionnairePage, setQuestionnairePage] = useState(1)
  const [pageMeta, setPageMeta] = useState({ articlePage: 1, articleLastPage: 1, articleTotal: 0, questionnairePage: 1, questionnaireLastPage: 1, questionnaireTotal: 0 })
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [highlightQuestionnaireId, setHighlightQuestionnaireId] = useState<number | null>(() => {
    const id = Number(new URLSearchParams(window.location.search).get('questionnaire_id'))
    return Number.isInteger(id) && id > 0 ? id : null
  })
  const lastListRefreshRef = useRef(0)

  const loadLists = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true)
    setError('')
    try {
      const response = await getLearningUpdates({
        articlePage,
        questionnairePage,
        perPage: LEARNING_PAGE_SIZE,
        search: debouncedSearch,
      })
      const articlePayload = record(response.articles)
      const questionnairePayload = record(response.questionnaires)
      const articleData = Array.isArray(articlePayload.data) ? articlePayload.data as KnowledgeArticle[] : []
      const questionnaireData = Array.isArray(questionnairePayload.data) ? questionnairePayload.data as LearningQuestionnaire[] : []
      const articleMeta = record(articlePayload.meta)
      const questionnaireMeta = record(questionnairePayload.meta)
      setArticles(canViewArticles ? articleData : [])
      setQuestionnaires(canViewQuestionnaires ? questionnaireData : [])
      setPageMeta({
        articlePage: Math.max(1, Number(articleMeta.current_page) || articlePage),
        articleLastPage: Math.max(1, Number(articleMeta.last_page) || 1),
        articleTotal: Number(articleMeta.total) || articleData.length,
        questionnairePage: Math.max(1, Number(questionnaireMeta.current_page) || questionnairePage),
        questionnaireLastPage: Math.max(1, Number(questionnaireMeta.last_page) || 1),
        questionnaireTotal: Number(questionnaireMeta.total) || questionnaireData.length,
      })
    } catch (err) {
      setError(getErrorMessage(err, 'Unable to load learning content.'))
    } finally {
      setLoading(false)
    }
  }, [articlePage, canViewArticles, canViewQuestionnaires, debouncedSearch, questionnairePage])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim())
      setArticlePage(1)
      setQuestionnairePage(1)
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [searchQuery])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const requestedTab = params.get('tab')
    if (requestedTab === 'questionnaires') setTab('questionnaires')
    const questionnaireId = Number(params.get('questionnaire_id'))
    if (Number.isInteger(questionnaireId) && questionnaireId > 0) {
      setTab('questionnaires')
      setHighlightQuestionnaireId(questionnaireId)
      setQuestionnairePage(1)
    }
  }, [location.search])

  useEffect(() => {
    if (!hasQuestionnaireTarget) return
    let active = true
    setLoading(true)
    setError('')
    getLearningQuestionnaires(1, LEARNING_PAGE_SIZE, String(requestedQuestionnaireId))
      .then(response => {
        if (!active) return
        const payload = record(response)
        const data = responseData<unknown>(response)
        const entries = Array.isArray(data) ? data : Array.isArray(record(data).data) ? record(data).data as unknown[] : []
        const questionnaire = entries.map(item => record(item) as unknown as LearningQuestionnaire)
          .find(item => item.id === requestedQuestionnaireId)
        if (!questionnaire) {
          setQuestionnaires([])
          setError('This questionnaire is not currently available in your assigned list. Check its scheduled availability or refresh later.')
          return
        }
        setQuestionnaires([questionnaire])
        const meta = record(payload.meta)
        setPageMeta(current => ({
          ...current,
          questionnairePage: 1,
          questionnaireLastPage: Math.max(1, Number(meta.last_page) || 1),
          questionnaireTotal: Number(meta.total) || 1,
        }))
        navigate('/learning?tab=questionnaires', { replace: true })
        window.setTimeout(() => document.getElementById(`questionnaire-${requestedQuestionnaireId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0)
      })
      .catch(err => {
        if (active) setError(getErrorMessage(err, 'Unable to refresh the assigned questionnaire list.'))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [hasQuestionnaireTarget, navigate, requestedQuestionnaireId])

  useEffect(() => {
    if (hasQuestionnaireTarget) return
    lastListRefreshRef.current = Date.now()
    void loadLists()

    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      // Focus and visibilitychange often fire together; avoid a duplicate EV API fetch.
      if (now - lastListRefreshRef.current < 2000) return
      lastListRefreshRef.current = now
      void loadLists(false)
    }

    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [hasQuestionnaireTarget, loadLists])

  const currentQuestion = questions[questionIndex]
  const normalizedSearch = searchQuery.trim()
  const articlePageCount = pageMeta.articleLastPage
  const questionnairePageCount = pageMeta.questionnaireLastPage
  const visibleArticles = articles
  const visibleQuestionnaires = questionnaires
  const categories = useMemo(() => Array.from(new Set(
    articles.map(article => article.category?.name).filter((name): name is string => !!name)
  )), [articles])

  const openArticle = async (article: KnowledgeArticle) => {
    setError('')
    if (!canViewArticles) return
    try {
      const response = await getKnowledgeArticle(article.id)
      const detail = responseData<KnowledgeArticle>(response)
      setSelectedArticle(detail && typeof detail === 'object'
        ? { ...article, ...detail, attachments: detail.attachments ?? article.attachments ?? [] }
        : article)
    } catch (err) {
      setError(getErrorMessage(err, 'Unable to open this lecture.'))
    }
  }

  const closeQuestionnaire = () => {
    setActiveQuestionnaire(null)
    setQuestions([])
    setQuestionIndex(0)
    setSelectedOption(null)
    setComplete(false)
    setQuestionnaireResult(null)
    setError('')
  }

  const openQuestionnaire = async (questionnaire: LearningQuestionnaire) => {
    setError('')
    if (!canViewQuestionnaires || !canAnswerQuestionnaires) return
    setSubmitting(true)
    try {
      await startLearningQuestionnaire(questionnaire.id)
      const detailResponse = await getLearningQuestionnaire(questionnaire.id)
      const detail = responseData<unknown>(detailResponse)
      const detailRecord = record(detail)
      const embedded = detailRecord.questions ?? detailRecord.questionnaire_questions
      let loadedQuestions: LearningQuestion[] = []

      if (Array.isArray(embedded)) {
        loadedQuestions = embedded.map((question: unknown) => normalizeQuestion(question)).filter((question: LearningQuestion | null): question is LearningQuestion => !!question)
      }

      if (loadedQuestions.length === 0) {
        const ids = getQuestionIds(detailResponse)
        if (ids.length > 0) {
          loadedQuestions = (await Promise.all(ids.map(async (id) => {
            const response = await getLearningQuestion(questionnaire.id, id)
            return normalizeQuestion(responseData(response), id)
          }))).filter((question): question is LearningQuestion => !!question)
        }
      }

      if (loadedQuestions.length === 0) {
        throw new Error('Question details are not available for this questionnaire.')
      }

      setActiveQuestionnaire(questionnaire)
      setQuestions(loadedQuestions)
      setQuestionIndex(0)
      setSelectedOption(null)
      setComplete(false)
      setQuestionnaireResult(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Unable to start this questionnaire.'))
    } finally {
      setSubmitting(false)
    }
  }

  const submitCurrentAnswer = async () => {
    if (!canAnswerQuestionnaires || !activeQuestionnaire || !currentQuestion || !selectedOption) return
    setSubmitting(true)
    setError('')
    try {
      await submitLearningAnswer(currentQuestion.id, selectedOption)
      if (questionIndex + 1 < questions.length) {
        setQuestionIndex(index => index + 1)
        setSelectedOption(null)
      } else {
        const response = await finishLearningQuestionnaire(activeQuestionnaire.id)
        const attempt = record(response.data)
        const result = record(attempt.result) as LearningQuestionnaireResult
        const responseCode = Number(response.code)
        if (
          response.status !== 'success'
          || !Number.isFinite(responseCode)
          || responseCode < 200
          || responseCode >= 300
          || !Object.keys(result).length
        ) {
          throw new Error(response.message || 'Eagle Vision did not return the completed questionnaire result.')
        }
        setQuestionnaireResult(result)
        setComplete(true)
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Unable to submit your answer. Please retry.'))
    } finally {
      setSubmitting(false)
    }
  }

  const renderContent = () => {
    if (selectedArticle) {
      return (
        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <button onClick={() => setSelectedArticle(null)} className="mb-4 flex items-center gap-1 text-sm font-medium text-primary-700">
            <ChevronLeft className="h-4 w-4" /> All lectures
          </button>
          {Array.isArray(selectedArticle.attachments) && selectedArticle.attachments.length > 0 && (
            <div className="mb-6 border-b border-gray-100 pb-5">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Attachments</h3>
              <ul className="space-y-3">
                {selectedArticle.attachments.map((attachment, index) => {
                  const href = safeAttachmentUrl(attachment.download_url || attachment.url)
                  const isImage = isImageAttachment(attachment.filename)
                  const previewUrl = isImage ? safeAttachmentUrl(attachment.url || attachment.download_url) : null
                  const AttachmentIcon = isImage ? FileImage : FileText
                  return (
                    <li key={attachment.id ?? `${attachment.filename}-${index}`} className="overflow-hidden rounded-lg bg-gray-50">
                      {previewUrl && (
                        <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="block bg-gray-100 p-2" aria-label={`Open image ${attachment.filename}`}>
                          <img src={previewUrl} alt={attachment.filename} loading="lazy" className="mx-auto max-h-80 max-w-full rounded object-contain" />
                        </a>
                      )}
                      <div className="flex items-center gap-3 p-3">
                        <AttachmentIcon className="h-5 w-5 shrink-0 text-primary-600" aria-label={attachment.icon || undefined} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-gray-800">{attachment.filename}</span>
                          {attachment.size != null && <span className="text-xs text-gray-500">{formatFileSize(attachment.size)}</span>}
                        </span>
                        {href ? (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="rounded-md p-2 text-primary-700 hover:bg-white" aria-label={`Download ${attachment.filename}`}>
                            <Download className="h-4 w-4" />
                          </a>
                        ) : <span className="text-xs text-gray-400">Unavailable</span>}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          <div className="mb-2 flex items-center gap-2 text-primary-600">
            <CheckSquare className="h-5 w-5" aria-hidden="true" />
            <p className="text-xs font-medium uppercase tracking-wide">{selectedArticle.category?.name || 'Knowledge Base'}</p>
          </div>
          <h2 className="text-xl font-bold text-gray-900">{selectedArticle.heading}</h2>
          <div className="mt-4 whitespace-pre-wrap break-words text-sm leading-7 text-gray-700">
            {htmlText(selectedArticle.description || 'No lecture content is available.')}
          </div>
        </section>
      )
    }

    if (activeQuestionnaire) {
      if (complete) {
        return (
          <section className="rounded-2xl bg-white p-8 text-center shadow-sm">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <h2 className="mt-3 text-lg font-semibold text-gray-900">Questionnaire submitted</h2>
            <p className="mt-1 text-sm text-gray-500">Your result has been saved by EagleVision.</p>
            {questionnaireResult && (
              <div className="mx-auto mt-5 grid max-w-md grid-cols-2 gap-3 text-left">
                <ResultValue label="Score" value={questionnaireResult.percentage !== undefined ? `${questionnaireResult.percentage}%` : '—'} />
                <ResultValue label="Correct answers" value={questionnaireResult.total_correct_answer ?? '—'} />
                <ResultValue label="Incorrect answers" value={questionnaireResult.total_false_answer ?? '—'} />
                <ResultValue label="Questions answered" value={
                  questionnaireResult.answered_questions !== undefined && questionnaireResult.total_questions !== undefined
                    ? `${questionnaireResult.answered_questions}/${questionnaireResult.total_questions}`
                    : '—'
                } />
                {questionnaireResult.completed_at && (
                  <p className="col-span-2 text-center text-xs text-gray-500">
                    Completed {new Date(questionnaireResult.completed_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}
            <button onClick={closeQuestionnaire} className="mt-5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white">Back to learning</button>
          </section>
        )
      }

      return (
        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <button onClick={closeQuestionnaire} className="mb-4 flex items-center gap-1 text-sm font-medium text-primary-700">
            <ChevronLeft className="h-4 w-4" /> Questionnaires
          </button>
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{htmlText(activeQuestionnaire.title)}</h2>
              <p className="mt-1 text-xs text-gray-500">Question {questionIndex + 1} of {questions.length}</p>
            </div>
            <span className="whitespace-nowrap rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              {questionIndex + 1}/{questions.length}
            </span>
          </div>
          <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-primary-600 transition-all" style={{ width: `${((questionIndex + 1) / questions.length) * 100}%` }} />
          </div>
          {currentQuestion ? (
            <>
              <h3 className="whitespace-pre-wrap text-base font-semibold leading-6 text-gray-900">{htmlText(currentQuestion.question)}</h3>
              {currentQuestion.note && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-500">{htmlText(currentQuestion.note)}</p>}
              <div className="mt-5 space-y-2">
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={`${currentQuestion.id}-${option.id || index}`}
                    type="button"
                    onClick={() => option.id > 0 && setSelectedOption(option.id)}
                    disabled={option.id <= 0 || submitting}
                    className={`w-full rounded-xl border p-3 text-left text-sm transition-colors ${
                      selectedOption === option.id
                        ? 'border-primary-500 bg-primary-50 text-primary-800'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-current text-xs font-semibold">{String.fromCharCode(65 + index)}</span>
                    {htmlText(option.option || option.name || option.title || 'Option')}
                  </button>
                ))}
              </div>
              {currentQuestion.options.some(option => option.id <= 0) && (
                <p className="mt-3 text-xs text-amber-700">Answer choices are missing option IDs. Please contact your administrator.</p>
              )}
              <button
                onClick={() => void submitCurrentAnswer()}
                disabled={!selectedOption || submitting}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {questionIndex + 1 === questions.length ? 'Submit questionnaire' : 'Submit answer'}
                {!submitting && <ChevronRight className="h-4 w-4" />}
              </button>
            </>
          ) : <p className="text-sm text-gray-500">Question not available.</p>}
        </section>
      )
    }

    if (tab === 'articles' && canViewArticles) {
      return (
        <div className="space-y-3">
          {visibleArticles.map(article => (
            <button key={article.id} onClick={() => void openArticle(article)} className="w-full rounded-xl bg-white p-4 text-left shadow-sm transition hover:shadow-md">
              <div className="flex items-start gap-3">
                <span className="rounded-lg bg-blue-50 p-2 text-blue-600"><CheckSquare className="h-5 w-5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-gray-900">{article.heading}</span>
                  <span className="mt-1 line-clamp-2 block text-sm text-gray-500">{htmlText(article.description || '')}</span>
                  <span className="mt-2 block text-xs font-medium text-primary-700">{article.category?.name || 'Lecture'} <ChevronRight className="inline h-3.5 w-3.5" /></span>
                </span>
              </div>
            </button>
          ))}
          {articles.length === 0 && <EmptyState title={normalizedSearch ? 'No matching lectures' : 'No lectures assigned'} description={normalizedSearch ? 'Try a different search term.' : 'There are no Knowledge Base lectures available right now.'} />}
          {pageMeta.articleTotal > 0 && <PaginationControls page={pageMeta.articlePage} pageCount={articlePageCount} total={pageMeta.articleTotal} onPageChange={setArticlePage} />}
          {categories.length > 0 && <p className="px-1 text-xs text-gray-400">Categories: {categories.join(', ')}</p>}
        </div>
      )
    }

    return (
      <div className="space-y-3">
        {visibleQuestionnaires.map(questionnaire => (
          <article id={`questionnaire-${questionnaire.id}`} key={questionnaire.id} className={`rounded-xl bg-white p-4 shadow-sm ${highlightQuestionnaireId === questionnaire.id ? 'ring-2 ring-primary-500' : ''}`}>
            <div className="flex items-start gap-3">
              <span className="rounded-lg bg-violet-50 p-2 text-violet-600"><ClipboardList className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900">{htmlText(questionnaire.title)}</h2>
                {questionnaire.exam_key && <p className="mt-1 text-xs text-gray-500">{htmlText(questionnaire.exam_key)}</p>}
                {questionnaire.duration !== undefined && <p className="mt-1 text-xs text-gray-500">{questionnaire.duration} minutes</p>}
                {highlightQuestionnaireId === questionnaire.id && <p className="mt-2 text-xs font-medium text-primary-700">New questionnaire assigned. Review the scheduled availability before starting.</p>}
                {canAnswerQuestionnaires && <button
                  onClick={() => void openQuestionnaire(questionnaire)}
                  disabled={submitting}
                  className="mt-3 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {submitting ? 'Loading…' : 'Start questionnaire'}
                </button>}
              </div>
            </div>
          </article>
        ))}
        {questionnaires.length === 0 && <EmptyState title={normalizedSearch ? 'No matching questionnaires' : 'No questionnaires assigned'} description={normalizedSearch ? 'Try a different search term.' : 'There are no questionnaires available right now.'} />}
        {pageMeta.questionnaireTotal > 0 && <PaginationControls page={pageMeta.questionnairePage} pageCount={questionnairePageCount} total={pageMeta.questionnaireTotal} onPageChange={setQuestionnairePage} />}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-5">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Learn and assess</p>
          <h1 className="text-2xl font-bold text-gray-900">Knowledge Base & Questionnaires</h1>
        </div>
        <button onClick={() => void loadLists()} className="rounded-lg p-2 text-gray-500 hover:bg-white" aria-label="Refresh learning content">
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      {!selectedArticle && !activeQuestionnaire && (canViewArticles || canViewQuestionnaires) && (
        <div className={`mb-4 grid ${canViewArticles && canViewQuestionnaires ? 'grid-cols-2' : 'grid-cols-1'} rounded-xl bg-gray-200 p-1`}>
          {canViewArticles && <button onClick={() => setTab('articles')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${tab === 'articles' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600'}`}>Knowledge Base</button>}
          {canViewQuestionnaires &&        <button onClick={() => setTab('questionnaires')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${tab === 'questionnaires' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600'}`}>Questionnaires</button>}

        </div>
      )}

      {!selectedArticle && !activeQuestionnaire && (tab === 'articles' ? canViewArticles : canViewQuestionnaires) && (
        <label className="mb-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
          <Search className="h-4 w-4 shrink-0 text-gray-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={event => {
              setSearchQuery(event.target.value)
              setArticlePage(1)
              setQuestionnairePage(1)
            }}
            placeholder={tab === 'articles' ? 'Search lectures, descriptions, categories…' : 'Search questionnaires…'}
            className="min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:ring-0"
            aria-label={tab === 'articles' ? 'Search Knowledge Base' : 'Search questionnaires'}
          />
          {searchQuery && <button type="button" onClick={() => setSearchQuery('')} className="text-xs font-medium text-primary-700">Clear</button>}
        </label>
      )}

      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-primary-600" /></div> : renderContent()}
    </div>
  )
}

function isImageAttachment(filename: string): boolean {
  return /\.(png|jpe?g|gif|webp)$/i.test(filename.split('?')[0])
}

function safeAttachmentUrl(value?: string | null): string | null {
  if (!value) return null
  try {
    const url = new URL(value, window.location.origin)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null
  } catch {
    return null
  }
}

function formatFileSize(size: number): string {
  if (!Number.isFinite(size) || size < 0) return ''
  if (size < 1024) return `${size} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = size / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`
}

function PaginationControls({ page, pageCount, total, onPageChange }: {
  page: number
  pageCount: number
  total: number
  onPageChange: (page: number) => void
}) {
  const start = total === 0 ? 0 : (page - 1) * LEARNING_PAGE_SIZE + 1
  const end = Math.min(page * LEARNING_PAGE_SIZE, total)
  return (
    <nav aria-label="Learning content pages" className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 shadow-sm">
      <span className="text-xs text-gray-500">{start}–{end} of {total}</span>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page <= 1} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-40">Previous</button>
        <span className="min-w-14 text-center text-xs text-gray-600">{page} / {pageCount}</span>
        <button type="button" onClick={() => onPageChange(Math.min(pageCount, page + 1))} disabled={page >= pageCount} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-40">Next</button>
      </div>
    </nav>
  )
}

function ResultValue({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 font-semibold text-gray-900">{value}</p>
    </div>
  )
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return <div className="rounded-xl bg-white px-5 py-10 text-center shadow-sm"><BookOpen className="mx-auto h-9 w-9 text-gray-300" /><h2 className="mt-3 font-semibold text-gray-800">{title}</h2><p className="mt-1 text-sm text-gray-500">{description}</p></div>
}
